import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addTask,
  archiveTask,
  deleteTask,
  duplicateTask,
  getKanbanBoard,
  moveTask,
  restoreTask,
  toggleTaskDone,
  updateTask,
} from "../../../../../services/tasksServices";

import { getAllAssociations } from "../../../../../services/associationsServices";
import { getAllProductsApp } from "../../../../../services/productsappServices";

import {
  EMPTY_TASK_STATS,
  TASK_STATUS_KANBAN_COLUMNS,
} from "../data/tasksKanbanData";

import {
  buildTaskColumns,
  buildTaskPayload,
  computePositionFromNeighbors,
  getProductAppIdFromDroppableId,
  getStatusFromDroppableId,
  isStatusDroppableId,
  moveTaskBetweenColumns,
  reorderTasks,
  sortTasks,
} from "../utils/tasksKanbanUtils";

const DEFAULT_FILTERS = {
  q: "",
  status: "all",
  priority: "all",
  product_app_id: "all",
  archived: "false",
  sort_due: "position",
  hideEmptyColumns: false,
  group_by: "product",
};

export default function useTasksKanban() {
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(EMPTY_TASK_STATS);
  const [columns, setColumns] = useState([]);

  const [productsApps, setProductsApps] = useState([]);
  const [associations, setAssociations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [allCardsExpanded, setAllCardsExpanded] = useState(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState(() => new Set());

  const [selectedTask, setSelectedTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [createColumn, setCreateColumn] = useState(null);

  const filteredColumns = useMemo(() => {
    const builtColumns = buildTaskColumns(columns, filters);
    const hasSearch = String(filters.q || "").trim().length > 0;

    if (hasSearch || filters.hideEmptyColumns) {
      return builtColumns.filter((column) => (column.tasks || []).length > 0);
    }

    return builtColumns;
  }, [columns, filters]);

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getKanbanBoard({
        q: "",
        archived: filters.archived || "false",
      });

      setBoard(data.board || null);
      setStats(data.stats || EMPTY_TASK_STATS);
      setColumns(Array.isArray(data.columns) ? data.columns : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement du tableau Kanban.");
    } finally {
      setLoading(false);
    }
  }, [filters.archived]);

  const loadReferences = useCallback(async () => {
    try {
      const [appsData, associationsData] = await Promise.all([
        getAllProductsApp(),
        getAllAssociations(),
      ]);

      setProductsApps(Array.isArray(appsData) ? appsData : []);
      setAssociations(Array.isArray(associationsData) ? associationsData : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des données de référence.");
    }
  }, []);

  useEffect(() => {
    loadBoard();
    loadReferences();
  }, [loadBoard, loadReferences]);

  const updateFilter = useCallback((name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const toggleAllCards = useCallback(() => {
    setAllCardsExpanded((current) => !current);
    setExpandedTaskIds(new Set());
  }, []);

  const toggleTaskExpanded = useCallback((taskId) => {
    if (!taskId) return;

    setExpandedTaskIds((current) => {
      const next = new Set(current);
      const key = String(taskId);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const openCreateTask = useCallback(() => {
    if (filters.group_by === "status") {
      setCreateColumn(TASK_STATUS_KANBAN_COLUMNS[0]);
      return;
    }

    setCreateColumn({
      id: "app-null",
      title: "Sans application",
      product_app_id: null,
      mode: "product",
    });
  }, [filters.group_by]);

  const replaceTaskInColumns = useCallback((updatedTask) => {
    if (!updatedTask?.id) return;

    setColumns((currentColumns) =>
      currentColumns.map((column) => ({
        ...column,
        tasks: (column.tasks || []).map((task) =>
          Number(task.id) === Number(updatedTask.id)
            ? { ...task, ...updatedTask }
            : task,
        ),
      })),
    );

    setSelectedTask((current) =>
      Number(current?.id) === Number(updatedTask.id)
        ? { ...current, ...updatedTask }
        : current,
    );
  }, []);

  const removeTaskFromColumns = useCallback((taskId) => {
    setColumns((currentColumns) =>
      currentColumns.map((column) => {
        const nextTasks = (column.tasks || []).filter(
          (task) => Number(task.id) !== Number(taskId),
        );

        return {
          ...column,
          tasks: nextTasks,
          count: nextTasks.length,
        };
      }),
    );

    setExpandedTaskIds((current) => {
      const next = new Set(current);
      next.delete(String(taskId));
      return next;
    });
  }, []);

  const optimisticMoveProductTask = useCallback(
    ({ taskId, destinationColumnId, newProductAppId, newPosition }) => {
      setColumns((currentColumns) => {
        let movedTask = null;

        const cleanedColumns = currentColumns.map((column) => {
          const nextTasks = (column.tasks || []).filter((task) => {
            const isMoved = Number(task.id) === Number(taskId);

            if (isMoved) {
              movedTask = task;
            }

            return !isMoved;
          });

          return {
            ...column,
            tasks: nextTasks,
            count: nextTasks.length,
          };
        });

        if (!movedTask) return currentColumns;

        return cleanedColumns.map((column) => {
          if (column.id !== destinationColumnId) return column;

          const updatedMovedTask = {
            ...movedTask,
            product_app_id: newProductAppId,
            position: newPosition,
          };

          const nextTasks = sortTasks(
            [...(column.tasks || []), updatedMovedTask],
            { sort_due: "position" },
          );

          return {
            ...column,
            tasks: nextTasks,
            count: nextTasks.length,
          };
        });
      });
    },
    [],
  );

  const optimisticMoveStatusTask = useCallback(
    ({ taskId, newStatus, newPosition }) => {
      setColumns((currentColumns) =>
        currentColumns.map((column) => ({
          ...column,
          tasks: (column.tasks || []).map((task) =>
            Number(task.id) === Number(taskId)
              ? {
                  ...task,
                  statut: newStatus,
                  position: newPosition,
                }
              : task,
          ),
        })),
      );
    },
    [],
  );

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;

      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const taskId = Number(String(draggableId).replace("task-", ""));

      const sourceColumn = filteredColumns.find(
        (column) => column.id === source.droppableId,
      );

      const destinationColumn = filteredColumns.find(
        (column) => column.id === destination.droppableId,
      );

      if (!sourceColumn || !destinationColumn) return;

      const previousColumns = columns;

      try {
        let orderedDestinationTasks = [];
        let movedTask = null;

        if (source.droppableId === destination.droppableId) {
          orderedDestinationTasks = reorderTasks(
            sourceColumn.tasks,
            source.index,
            destination.index,
          );
          movedTask = orderedDestinationTasks[destination.index];
        } else {
          const moveResult = moveTaskBetweenColumns({
            sourceColumn,
            destinationColumn,
            sourceIndex: source.index,
            destinationIndex: destination.index,
          });

          movedTask = moveResult.movedTask;
          orderedDestinationTasks = moveResult.orderedDestinationTasks;
        }

        if (!movedTask) return;

        const newPosition = computePositionFromNeighbors(
          orderedDestinationTasks,
          destination.index,
        );

        if (isStatusDroppableId(destination.droppableId)) {
          const newStatus = getStatusFromDroppableId(destination.droppableId);

          optimisticMoveStatusTask({
            taskId,
            newStatus,
            newPosition,
          });

          const updatedTask = await updateTask(
            taskId,
            buildTaskPayload({
              ...movedTask,
              statut: newStatus,
              position: newPosition,
            }),
          );

          if (updatedTask?.id) {
            replaceTaskInColumns({
              ...updatedTask,
              statut: newStatus,
              position: newPosition,
            });
          }

          toast.success("Statut de la carte mis à jour.");
          return;
        }

        const newProductAppId = getProductAppIdFromDroppableId(
          destination.droppableId,
        );

        optimisticMoveProductTask({
          taskId,
          destinationColumnId: destinationColumn.id,
          newProductAppId,
          newPosition,
        });

        const updatedTask = await moveTask(taskId, {
          product_app_id: newProductAppId,
          position: newPosition,
        });

        if (updatedTask?.id) {
          replaceTaskInColumns(updatedTask);
        }

        toast.success("Carte déplacée.");
      } catch (error) {
        console.error(error);
        setColumns(previousColumns);
        toast.error("Erreur lors du déplacement de la carte.");
      }
    },
    [
      columns,
      filteredColumns,
      optimisticMoveProductTask,
      optimisticMoveStatusTask,
      replaceTaskInColumns,
    ],
  );

  const handleCreateTask = useCallback(
    async (formData) => {
      try {
        setSaving(true);

        const payload = buildTaskPayload(formData);

        if (!payload.titre) {
          toast.error("Le titre de la tâche est obligatoire.");
          return null;
        }

        const createdTask = await addTask(payload);

        toast.success("Tâche ajoutée.");
        await loadBoard();

        return createdTask;
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Erreur lors de la création de la tâche.");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [loadBoard],
  );

  const handleUpdateTask = useCallback(
    async (taskId, formData) => {
      try {
        setSaving(true);

        const payload = buildTaskPayload(formData);

        if (!payload.titre) {
          toast.error("Le titre de la tâche est obligatoire.");
          return null;
        }

        const updatedTask = await updateTask(taskId, payload);

        if (updatedTask?.id) {
          replaceTaskInColumns(updatedTask);
          setSelectedTask(updatedTask);
        }

        await loadBoard();

        toast.success("Tâche mise à jour.");
        return updatedTask;
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Erreur lors de la mise à jour.");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [loadBoard, replaceTaskInColumns],
  );

  const handleToggleDone = useCallback(
    async (task) => {
      try {
        const updatedTask = await toggleTaskDone(task.id);

        if (updatedTask?.id) {
          replaceTaskInColumns(updatedTask);
          toast.success("Statut de la tâche mis à jour.");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors du changement de statut.");
      }
    },
    [replaceTaskInColumns],
  );

  const handleDuplicateTask = useCallback(
    async (task) => {
      try {
        await duplicateTask(task.id);
        toast.success("Tâche dupliquée.");
        await loadBoard();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la duplication.");
      }
    },
    [loadBoard],
  );

  const handleArchiveTask = useCallback(
    async (task) => {
      try {
        await archiveTask(task.id);
        removeTaskFromColumns(task.id);
        setSelectedTask(null);
        toast.success("Tâche archivée.");
        await loadBoard();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l'archivage.");
      }
    },
    [loadBoard, removeTaskFromColumns],
  );

  const handleRestoreTask = useCallback(
    async (task) => {
      try {
        await restoreTask(task.id);
        toast.success("Tâche restaurée.");
        await loadBoard();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la restauration.");
      }
    },
    [loadBoard],
  );

  const handleDeleteTask = useCallback(
    async (task) => {
      try {
        await deleteTask(task.id);
        removeTaskFromColumns(task.id);
        setSelectedTask(null);
        setTaskToDelete(null);
        toast.success("Tâche supprimée.");
        await loadBoard();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la suppression.");
      }
    },
    [loadBoard, removeTaskFromColumns],
  );

  return {
    board,
    stats,
    columns,
    filteredColumns,
    productsApps,
    associations,
    loading,
    saving,

    filters,
    updateFilter,
    resetFilters,

    allCardsExpanded,
    expandedTaskIds,
    toggleAllCards,
    toggleTaskExpanded,

    selectedTask,
    setSelectedTask,

    taskToDelete,
    setTaskToDelete,

    createColumn,
    setCreateColumn,
    openCreateTask,

    loadBoard,
    handleDragEnd,
    handleCreateTask,
    handleUpdateTask,
    handleToggleDone,
    handleDuplicateTask,
    handleArchiveTask,
    handleRestoreTask,
    handleDeleteTask,
  };
}